/* eslint-disable no-underscore-dangle,prefer-promise-reject-errors */
import { useState } from "react";
import { useMsal } from "@azure/msal-react";
import axios, { AxiosRequestTransformer } from "axios";
import { AuthenticationResult } from "@azure/msal-browser";
import { loginRequest } from "./AuthConfig";
import { transformRequestDatesToApiFormat } from "../Axios/AxiosRequestTransformers";

interface RequestInterceptorProps {
  children: JSX.Element;
}

function AxiosRequestInterceptor({ children }: RequestInterceptorProps) {
  const { instance } = useMsal();
  const [token, setToken] = useState<string>("");

  async function acquireTokenFromAd(): Promise<AuthenticationResult> {
    try {
      return await instance.acquireTokenSilent({
        ...loginRequest,
      });
    } catch (error: any) {
      console.error("acquireTokenFromAd acquireTokenSilent caught error", error.message);
      if (
        error.errorCode === "consent_required" ||
        error.errorCode === "interaction_required" ||
        error.errorCode === "login_required"
      ) {
        return instance.acquireTokenPopup(loginRequest);
      }

      throw new Error("unable to acquire token ");
    }
  }

  async function acquireToken(): Promise<string> {
    if (token) {
      return token;
    }

    const response = await acquireTokenFromAd();
    setToken(response.accessToken);
    return response.accessToken;
  }

  // Request interceptor for API calls
  axios.interceptors.request.use(async (config) => {
    const accessToken = await acquireToken();
    const bearer = `Bearer ${accessToken}`;
    // eslint-disable-next-line no-param-reassign
    config.headers = {
      ...config.headers,
      Authorization: bearer,
    };

    // eslint-disable-next-line no-param-reassign
    config.transformRequest = [
      // Transform dates to yyyy-MM-dd format before sending to API.
      // Otherwise, JSON.stringify() will transform the local date to UTC date, which might cause one-off errors.
      // E.g. 2021-01-01 00:00 (GMT+1) would be transformed to 2020-12-31 23:00 (UTC).
      // See https://stackoverflow.com/questions/70689305/customizing-date-serialization-in-axios
      (data, headers) => transformRequestDatesToApiFormat(data, headers),
      ...defaultTransformers(),
    ];

    return config;
  });

  // Response interceptor for API calls
  axios.interceptors.response.use(
    (response) => {
      return response;
    },
    async function (error: any) {
      const originalRequest = error.config;
      if (error.response?.status === 403 && !originalRequest._retry) {
        originalRequest._retry = true;
        const { accessToken } = await acquireTokenFromAd();
        setToken(accessToken);
        axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
        return axios(originalRequest);
      }

      if (error.response?.status === 400 && !originalRequest._retry) {
        originalRequest._retry = true;
        const responseMessage = `${error.response?.data?.message}`;
        console.error(`Client error: ${responseMessage}`, error);
        return Promise.reject({
          ...error,
          message: responseMessage,
        });
      }

      if (error.response?.status === 500 && !originalRequest._retry) {
        originalRequest._retry = true;
        const responseMessage = `Server error: ${error.message}`;
        console.error(responseMessage, error);
        return Promise.reject({
          ...error,
          message: responseMessage,
        });
      }

      return Promise.reject(error);
    }
  );

  return children;
}

const defaultTransformers = (): AxiosRequestTransformer[] => {
  const { transformRequest } = axios.defaults;
  return !transformRequest
    ? []
    : transformRequest instanceof Array
    ? transformRequest
    : [transformRequest];
};

export default AxiosRequestInterceptor;
